import React, { useState, useMemo } from "react";
import useChargingSessionsByGroup from "../hooks/useChargingSessionsByGroup";
import Filters from "../components/FiltersComponent";
import SessionsTable from "../components/SessionsTable";
import { currentGroupId } from "../utils/access";
import { BatteryCharging, RefreshCw } from "lucide-react";
import { PageHeader, PageShell } from "../components/ui/DataPage";

const updatedTimeFormatter = new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit" });
const updatedDateTimeFormatter = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

// "today at 09:42 pm", or "30 Oct, 09:42 pm" for older data
const formatUpdatedAt = (date) =>
  date.toDateString() === new Date().toDateString()
    ? `today at ${updatedTimeFormatter.format(date)}`
    : updatedDateTimeFormatter.format(date);

const ChargingSessionsPage = () => {
  const [customRange, setCustomRange] = useState(null);
  const [selectedStations, setSelectedStations] = useState([]);

  const groupId = currentGroupId();
  const {
    sessions: chargingSessionData = [],
    isLoading,
    error,
    settlementAvailable,
    lastUpdated,
    refetch,
  } = useChargingSessionsByGroup(groupId, customRange, { withSettlement: true });

  // Get unique stations
  const uniqueStations = useMemo(() => {
    if (!chargingSessionData?.length) return [];
    return Array.from(new Set(chargingSessionData.map((s) => s.station_id || "Unknown"))).sort();
  }, [chargingSessionData]);

  // Filter sessions
  const filteredSessions = useMemo(() => {
    return chargingSessionData.filter((session) => {
      if (customRange) {
        const sessionDate = new Date(session.session_start);
        const from = new Date(customRange.fromDate);
        const to = new Date(customRange.toDate);
        if (sessionDate < from || sessionDate > to) return false;
      }
      if (selectedStations.length > 0 && !selectedStations.includes(session.station_id))
        return false;
      return true;
    });
  }, [chargingSessionData, customRange, selectedStations]);

  const activeFiltersCount = (customRange ? 1 : 0) + (selectedStations.length > 0 ? 1 : 0);

  // --- Render ---
  return (
    <PageShell>
        <PageHeader
          icon={BatteryCharging}
          title="Charging Sessions"
          description="View and filter charging session history"
          actions={
            <>
              <span className="text-xs text-neutral-500">
                {isLoading ? "Updating…" : lastUpdated ? `Last updated ${formatUpdatedAt(lastUpdated)}` : ""}
              </span>
              <button type="button" onClick={refetch} disabled={isLoading} className="btn btn-secondary disabled:opacity-60">
                <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </>
          }
        />

        {/* Loading / Error banners — show but do not hide filters
        {isLoading && (
          <div className="mb-4 p-3 rounded-md bg-yellow-50 text-yellow-800">Loading sessions...</div>
        )}
        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700">Error loading sessions</div>
        )} */}

        {/* Filters */}
        <Filters
          customRange={customRange}
          selectedStations={selectedStations}
          uniqueStations={uniqueStations}
          onCustomRangeChange={setCustomRange}
          onSelectedStationsChange={setSelectedStations}
          activeFiltersCount={activeFiltersCount}
        />

        {/* Table */}
        <SessionsTable
          sessions={filteredSessions}
          totalCount={chargingSessionData.length}
          isLoading={isLoading}
          settlementAvailable={settlementAvailable}
        />
    </PageShell>
  );
};

export default ChargingSessionsPage;